import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { AlertTriangle, X } from "lucide-react";

/**
 * Reusable confirmation dialog.
 * Usage:
 *   <ConfirmDialog
 *     open={showConfirm}
 *     onOpenChange={setShowConfirm}
 *     title="Remover lançamento?"
 *     description="Essa ação não pode ser desfeita."
 *     onConfirm={() => doSomething()}
 *   />
 */
const ConfirmDialog = ({
  open,
  onOpenChange,
  title = "Confirmar exclusão",
  description = "Tem certeza que deseja remover este item? Essa ação não pode ser desfeita.",
  confirmLabel = "Sim, remover",
  cancelLabel = "Cancelar",
  onConfirm,
  variant = "danger", // "danger" | "warning"
  seriesLabel,
  onConfirmSeries,
}) => {
  const handleConfirm = () => {
    onConfirm?.();
    onOpenChange(false);
  };

  const handleConfirmSeries = () => {
    onConfirmSeries?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-display text-xl">
            <div className={`h-10 w-10 rounded-full grid place-items-center shrink-0 ${
              variant === "danger"
                ? "bg-rose-500/20 text-rose-400"
                : "bg-amber-500/20 text-amber-400"
            }`}>
              <AlertTriangle className="h-5 w-5" />
            </div>
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-400 mt-2">
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 pt-4">
          {seriesLabel && onConfirmSeries && (
            <Button
              type="button"
              onClick={handleConfirmSeries}
              className="w-full font-semibold text-white bg-rose-700 hover:bg-rose-800 border-rose-700"
              data-testid="confirm-series"
            >
              {seriesLabel}
            </Button>
          )}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              data-testid="confirm-cancel"
            >
              <X className="h-4 w-4 mr-1.5" />
              {cancelLabel}
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              className={`flex-1 font-semibold text-white ${
                variant === "danger"
                  ? "bg-rose-600 hover:bg-rose-700 border-rose-600"
                  : "bg-amber-600 hover:bg-amber-700 border-amber-600"
              }`}
              data-testid="confirm-ok"
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
